import { CdkDrag, CdkDragHandle, Point } from '@angular/cdk/drag-drop';

import { Component, ElementRef, OnInit, ViewChild, input, inject, signal } from '@angular/core';

import { CommonModule } from '@angular/common';

import { of } from 'rxjs';

import { PanelService } from './panel.service';

@Component({
  selector: 'vb-lxp-panel',
  imports: [CdkDrag, CdkDragHandle, CommonModule],
  templateUrl: './vb-lxp-panel.component.html',
  styleUrl: './vb-lxp-panel.component.css',
  host: {
    '[style.position]': `!isPinned() ? 'absolute' : ''`,
  },
})
export class VbLxpPanelComponent implements OnInit {
  @ViewChild('panel') panel: ElementRef | undefined;
  @ViewChild('panelDragHandle') panelDragHandle: CdkDrag | undefined;
  private hostElement = inject(ElementRef<HTMLElement>);

  public panelService = inject(PanelService);

  public disablePin = input(false);
  public defaultOpenState = input(false);
  public panelKey = input.required<string>();
  public panelClass = input('');
  public panelHeaderClass = input('');
  public panelBodyClass = input('');

  public isOpen$ = of(false);
  public isOpen = signal(false);

  public isPinned = signal(true);


  private headerHeight = 64; 

  public constrainPosition = (point: Point): Point => {
    const rootStyles = getComputedStyle(document.documentElement);
    const headerHeightVar = rootStyles.getPropertyValue('--header-height').trim();
    
    if (headerHeightVar) {
      this.headerHeight = parseInt(headerHeightVar, 10) || 64;
    }

    const panelHeight = this.panel?.nativeElement?.offsetHeight || 200;
    const panelWidth = this.panel?.nativeElement?.offsetWidth || 300;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxX = Math.max(0, viewportWidth - panelWidth);
    const maxY = viewportHeight - panelHeight;

    const constrainedX = Math.min(Math.max(point.x, 0), maxX);
    const constrainedY = Math.min(Math.max(point.y, this.headerHeight), maxY);

    return {
      x: constrainedX,
      y: constrainedY,
    };
  };

  public ngOnInit(): void {
    this.panelService.registerPanel(this.panelKey());
    if (this.defaultOpenState()) {
      this.panelService.openPanel(this.panelKey());
      this.isOpen.set(true);
    }
    this.isOpen$ = this.panelService.getPanelState(this.panelKey());
    this.isOpen$.subscribe((isOpen) => {
      if (isOpen && !this.isOpen()) {
        this.resetState();
      }
      this.isOpen.set(isOpen);
    });
  }

  private resetState() {
    this.isPinned.set(true);
    this.panelDragHandle?.reset();
    const hostEl = this.hostElement.nativeElement;
    hostEl.style.left = '';
    hostEl.style.top = '';
    hostEl.style.right = '';
    if (this.panel) {
      this.panel!.nativeElement.style.width = '';
      this.panel!.nativeElement.style.height = '';
    }
  }

  public onPinClicked() {
    if (this.isPinned()) {
      const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
      this.isPinned.set(false);

      requestAnimationFrame(() => {
        const rootStyles = getComputedStyle(document.documentElement);
        const headerHeightVar = rootStyles.getPropertyValue('--header-height').trim();

        if (headerHeightVar) {
          this.headerHeight = parseInt(headerHeightVar, 10) || 64;
        }
        const hostEl = this.hostElement.nativeElement;
        const panelWidth = this.panel?.nativeElement?.offsetWidth || hostRect.width || 400;
        const panelHeight = this.panel?.nativeElement?.offsetHeight || hostRect.height || 400;
        const maxX = Math.max(0, window.innerWidth - panelWidth);
        const maxY = window.innerHeight - panelHeight;

        const safeLeft = Math.min(Math.max(hostRect.left, 0), maxX);
        const safeTop = Math.min(Math.max(hostRect.top, this.headerHeight), maxY);

        hostEl.style.left = `${Math.round(safeLeft)}px`;
        hostEl.style.top = `${Math.round(safeTop)}px`;
        hostEl.style.right = 'auto';
      });
      return;
    }

    this.isPinned.set(true);
    if (this.isPinned()) {
      this.resetState();
    }
  }

  public closePanel() {
    this.panelService.closePanel(this.panelKey());
  }
}
